import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { Button, Checkbox, InfoBox, Label, StatusBadge, cn } from '@immo/ui-kit';
import { useTranslations } from '../use-translations';
import { ColumnGrip } from './column-grip';
import { useColumnDrag, type ColumnDrag } from './use-column-drag';

export type ColumnPanelRow = {
  readonly id: string;
  readonly label: string;
  readonly visible: boolean;
  readonly alwaysVisible: boolean;
  readonly locked: boolean;
  readonly isNew: boolean;
};

type ColumnsPanelProps = {
  readonly rows: ReadonlyArray<ColumnPanelRow>;
  /** Anchor. The panel measures it and returns focus to it. */
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly onSetVisible: (id: string, visible: boolean) => void;
  readonly onReset: () => void;
  readonly onReorder: (order: ReadonlyArray<string>) => void;
  readonly onClose: () => void;
};

type PanelPosition = { readonly top: number };

const EDGE = 8;
const MIN_BOTTOM_ROOM = 200;

function positionStyle(position: PanelPosition | null): CSSProperties {
  if (position === null) return { visibility: 'hidden', right: EDGE, top: EDGE };

  // Never taller than the room under `top`, so the footer buttons stay on screen.
  return { right: EDGE, top: position.top, maxHeight: `min(70vh, 560px, calc(100vh - ${position.top + EDGE}px))` };
}

export function ColumnsPanel({ rows, triggerRef, onSetVisible, onReset, onReorder, onClose }: ColumnsPanelProps) {
  const t = useTranslations('components.column-preference');
  const intl = useIntl();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  // `rows` lists every column, hidden ones in place, so this IS the full order.
  const order = useMemo(() => rows.map((row) => row.id), [rows]);
  const drag = useColumnDrag(order, onReorder);

  // Docked right, top clamped on screen, re-measured on resize. NO scroll
  // listener: the panel is worked in while the list moves behind it.
  useLayoutEffect(() => {
    const reposition = () => {
      const trigger = triggerRef.current;

      if (trigger === null) return;

      const { bottom } = trigger.getBoundingClientRect();

      setPosition({ top: Math.max(EDGE, Math.min(bottom + EDGE, window.innerHeight - MIN_BOTTOM_ROOM)) });
    };

    reposition();
    window.addEventListener('resize', reposition);

    return () => window.removeEventListener('resize', reposition);
  }, [triggerRef]);

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('input[type="checkbox"], button')?.focus();
  }, []);

  // Capture phase, because a bubble-phase document listener is a contract with
  // every ancestor this panel is ever rendered under (BL-023).
  useEffect(() => {
    const dismiss = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (target === null) return;

      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;

      onClose();
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.stopPropagation();
      triggerRef.current?.focus();
      onClose();
    };

    document.addEventListener('mousedown', dismiss, true);
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('mousedown', dismiss, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, triggerRef]);

  const newNames = rows.filter((row) => row.isNew).map((row) => row.label);
  // The always-visible and locked columns are not switchable, so they must not count towards the floor.
  const noneSwitchableVisible = rows.every((row) => row.alwaysVisible || row.locked || !row.visible);

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('panel.title')}
      style={positionStyle(position)}
      className="fixed z-50 flex w-80 flex-col rounded-lg border border-border bg-surface shadow-md"
    >
      <div className="border-b border-border p-4">
        <p className="text-base font-medium text-text-primary">{t('panel.title')}</p>
        <p className="mt-1 text-base-sm text-text-secondary">{t('panel.hint')}</p>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto py-1"
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) drag.clear();
        }}
      >
        {newNames.length > 0 && (
          <InfoBox type="info" className="mx-3 mb-2">
            {t('panel.new-columns', {
              count: newNames.length,
              names: intl.formatList(newNames, { type: 'conjunction' }),
            })}
          </InfoBox>
        )}
        {noneSwitchableVisible && (
          <InfoBox type="danger" className="mx-3 mb-2">
            {t('panel.min-visible')}
          </InfoBox>
        )}
        {rows.map((row, index) => (
          <ColumnsPanelRow
            key={row.id}
            row={row}
            nextId={rows[index + 1]?.id ?? null}
            isLast={index === rows.length - 1}
            draggable={rows.filter((row) => !row.locked).length > 1}
            drag={drag}
            onSetVisible={onSetVisible}
          />
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-border px-3 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={onReset}>
          {t('panel.reset')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            triggerRef.current?.focus();
            onClose();
          }}
        >
          {t('panel.done')}
        </Button>
      </div>
    </div>,
    document.body,
  );
}

type ColumnsPanelRowProps = {
  readonly row: ColumnPanelRow;
  readonly nextId: string | null;
  readonly isLast: boolean;
  readonly draggable: boolean;
  readonly drag: ColumnDrag;
  readonly onSetVisible: (id: string, visible: boolean) => void;
};

function ColumnsPanelRow({ row, nextId, isLast, draggable, drag, onSetVisible }: ColumnsPanelRowProps) {
  const t = useTranslations('components.column-preference');
  const inputId = `column-toggle-${row.id}`;
  const isDragged = drag.draggedId === row.id;
  const fixed = row.alwaysVisible || row.locked;
  const before = drag.draggedId !== null && drag.target?.beforeId === row.id;
  const afterLast = drag.draggedId !== null && isLast && drag.target !== null && drag.target.beforeId === null;

  return (
    // Hidden means PALE and in place, never removed: a member looking for the
    // column they switched off finds it where they left it.
    <div
      onDragOver={
        row.locked
          ? undefined
          : (event) => {
              if (drag.draggedId === null) return;

              // Without preventDefault the browser refuses the drop and `onDrop` never fires.
              event.preventDefault();

              const rect = event.currentTarget.getBoundingClientRect();

              drag.hover({ beforeId: event.clientY < rect.top + rect.height / 2 ? row.id : nextId });
            }
      }
      onDrop={
        row.locked
          ? undefined
          : (event) => {
              event.preventDefault();
              drag.drop();
            }
      }
      className={cn(
        'group/row flex items-center gap-2 border border-transparent px-3 py-2',
        !row.visible && 'opacity-60',
        isDragged && 'border-interactive bg-surface shadow-sm',
        before && 'border-t-2 border-t-interactive',
        afterLast && 'border-b-2 border-b-interactive',
      )}
    >
      {draggable && !row.locked && (
        <ColumnGrip
          columnLabel={row.label}
          onDragStart={() => drag.start(row.id)}
          onDragEnd={drag.clear}
          className="group-hover/row:opacity-100"
        />
      )}
      {fixed ? (
        <span className="min-w-0 flex-1 truncate text-base-sm text-text-primary">{row.label}</span>
      ) : (
        <Label htmlFor={inputId} className="min-w-0 flex-1 truncate text-base-sm">
          {row.label}
        </Label>
      )}
      {row.isNew && <StatusBadge tone="info" label={t('panel.new-badge')} />}
      {fixed ? (
        // No disabled checkbox: a greyed-out control invites a click and explains nothing.
        <span className="shrink-0 text-base-sm text-text-tertiary">{t('panel.always-visible')}</span>
      ) : (
        <Checkbox id={inputId} checked={row.visible} onCheckedChange={(checked) => onSetVisible(row.id, checked)} />
      )}
    </div>
  );
}
